import type { TabItem, TabsAlign, TabsProps } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import { ColorField, NumberField, SelectField, TextField } from "./fields.js";
import type { WidgetInspectorProps } from "./registry.js";

const ALIGN = ["start", "center", "end", "stretch"] as const;

function newItemId(): string {
  return `t-${Math.random().toString(36).slice(2, 8)}`;
}

export function TabsInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as TabsProps;
  const items = p.items ?? [];

  const setItems = (next: TabItem[]) => update(widget.id, { items: next });

  const addItem = () =>
    setItems([...items, { id: newItemId(), title: `Tab ${items.length + 1}`, body: "" }]);

  const updateItem = (id: string, patch: Partial<TabItem>) =>
    setItems(items.map((it) => (it.id === id ? { ...it, ...patch } : it)));

  const removeItem = (id: string) => setItems(items.filter((it) => it.id !== id));

  const moveItem = (id: string, dir: "up" | "down") => {
    const idx = items.findIndex((it) => it.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= items.length) return;
    const next = items.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setItems(next);
  };

  return (
    <>
      <SelectField
        label="Alignment"
        value={p.align ?? "start"}
        options={ALIGN}
        onChange={(v) => update(widget.id, { align: v as TabsAlign })}
      />
      <NumberField
        label="Default active (0-indexed)"
        value={p.defaultIndex ?? 0}
        min={0}
        max={Math.max(0, items.length - 1)}
        onChange={(v) => update(widget.id, { defaultIndex: v })}
      />
      <ColorField
        label="Accent colour"
        value={p.accent ?? "#111827"}
        onChange={(v) => update(widget.id, { accent: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Tabs ({items.length})
      </div>

      {items.map((it, i) => (
        <div
          key={it.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveItem(it.id, "down")}
              disabled={i === items.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === items.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeItem(it.id)}
              title="Delete tab"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>
          <TextField
            label={`Tab ${i + 1} title`}
            value={it.title}
            onChange={(v) => updateItem(it.id, { title: v })}
          />
          <TextField
            label="Body"
            value={it.body}
            onChange={(v) => updateItem(it.id, { body: v })}
            multiline
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addItem}
          style={{ width: "100%" }}
        >
          + Add tab
        </button>
      </div>
    </>
  );
}
